'use client'

/**
 * LoadingState — componente reutilizável para feedback de carregamento.
 *
 * Variantes:
 * - `fullScreen` (default): overlay centralizado, bom para loading.tsx do App Router
 *   e operações que bloqueiam a tela.
 * - `inline`: dentro de cards/seções, não ocupa a tela toda.
 * - `skeleton`: blocos cinzas animados, bom para listas/tabelas durante fetch.
 *
 * Task #195 / F13.6 — padronização visual de estados de loading.
 */

interface LoadingStateProps {
  variant?: 'fullScreen' | 'inline' | 'skeleton'
  message?: string
  /** Quando variant='skeleton', número de blocos a renderizar */
  skeletonRows?: number
  className?: string
}

export function LoadingState({
  variant = 'fullScreen',
  message = 'Carregando...',
  skeletonRows = 3,
  className = '',
}: LoadingStateProps) {
  if (variant === 'skeleton') {
    return (
      <div className={`space-y-3 ${className}`} role="status" aria-label={message}>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <div key={i} className="h-12 w-full animate-pulse rounded-md bg-gray-200" />
        ))}
        <span className="sr-only">{message}</span>
      </div>
    )
  }

  if (variant === 'inline') {
    return (
      <div
        className={`flex items-center justify-center gap-3 py-6 ${className}`}
        role="status"
        aria-label={message}
      >
        <Spinner />
        <span className="text-sm text-gray-600">{message}</span>
      </div>
    )
  }

  // fullScreen (default)
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm ${className}`}
      role="status"
      aria-label={message}
    >
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <span className="text-base font-medium text-gray-700">{message}</span>
      </div>
    </div>
  )
}

function Spinner({ size = 'md' }: { size?: 'md' | 'lg' }) {
  const dim = size === 'lg' ? 'h-10 w-10 border-4' : 'h-6 w-6 border-2'
  return (
    <div
      className={`${dim} border-t-primary animate-spin rounded-full border-gray-200`}
      aria-hidden="true"
    />
  )
}

export default LoadingState
